import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { formatMoney } from "@/lib/money";
import { Search, Snowflake, KeyRound, DollarSign, Loader2 } from "lucide-react";

interface UserRow {
  user_id: string;
  cashtag: string;
  display_name: string;
  balance_cents: number;
  hustle_score: number;
  hustle_level: string;
  is_merchant: boolean;
  is_creator: boolean;
  frozen: boolean;
  created_at: string;
}

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<UserRow | null>(null);
  const [detail, setDetail] = useState<any>(null);

  const [freezeOpen, setFreezeOpen] = useState(false);
  const [freezeReason, setFreezeReason] = useState("");
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [acting, setActing] = useState(false);

  const search = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("admin_search_users", { _q: q || null, _limit: 50 });
    setLoading(false);
    if (error) {
      toast({ title: "Search failed", description: error.message, variant: "destructive" });
      return;
    }
    setRows((data ?? []) as UserRow[]);
  };

  useEffect(() => { search(); }, []);

  const openDetail = async (row: UserRow) => {
    setSelected(row);
    setDetail(null);
    const { data, error } = await supabase.rpc("admin_get_user_detail", { _user_id: row.user_id });
    if (error) { toast({ title: "Failed", description: error.message, variant: "destructive" }); return; }
    setDetail(data);
  };

  const doFreeze = async () => {
    if (!selected) return;
    setActing(true);
    const { error } = await supabase.rpc("admin_freeze_wallet", {
      _user_id: selected.user_id, _reason: freezeReason,
    });
    setActing(false);
    if (error) { toast({ title: "Freeze failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Wallet frozen" });
    setFreezeOpen(false); setFreezeReason("");
    openDetail(selected); search();
  };

  const doUnfreeze = async () => {
    if (!selected) return;
    setActing(true);
    const { error } = await supabase.rpc("admin_unfreeze_wallet", { _user_id: selected.user_id });
    setActing(false);
    if (error) { toast({ title: "Unfreeze failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Wallet unfrozen" });
    openDetail(selected); search();
  };

  const doResetPin = async () => {
    if (!selected) return;
    if (!confirm("Reset this user's PIN? They will need to set a new one.")) return;
    setActing(true);
    const { error } = await supabase.rpc("admin_reset_pin", {
      _user_id: selected.user_id, _reason: "Admin reset via console",
    });
    setActing(false);
    if (error) { toast({ title: "Reset failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "PIN reset" });
  };

  const doAdjust = async () => {
    if (!selected) return;
    const dollars = Number(adjustAmount);
    if (!Number.isFinite(dollars) || dollars === 0) {
      toast({ title: "Enter a non-zero amount (use - to deduct)", variant: "destructive" });
      return;
    }
    setActing(true);
    const { error } = await supabase.rpc("admin_adjust_balance", {
      _user_id: selected.user_id,
      _delta_cents: Math.round(dollars * 100),
      _reason: adjustReason,
    });
    setActing(false);
    if (error) { toast({ title: "Adjustment failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Balance adjusted" });
    setAdjustOpen(false); setAdjustAmount(""); setAdjustReason("");
    openDetail(selected); search();
  };

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold">Users</h1>
        <p className="text-sm text-muted-foreground">Search by cashtag, name, or user ID.</p>
      </div>

      <div className="flex gap-2 mb-4 max-w-xl">
        <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()} />
        <Button onClick={search} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      <div className="space-y-2">
        {rows.map((r) => (
          <Card key={r.user_id} className="p-3 flex items-center justify-between hover:bg-muted/40 cursor-pointer"
                onClick={() => openDetail(r)}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">${r.cashtag}</span>
                <span className="text-sm text-muted-foreground">{r.display_name}</span>
                {r.frozen && <Badge variant="destructive">FROZEN</Badge>}
                {r.is_merchant && <Badge variant="secondary">Merchant</Badge>}
                {r.is_creator && <Badge variant="secondary">Creator</Badge>}
                <Badge variant="outline">{r.hustle_level}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{r.user_id}</p>
            </div>
            <div className="text-right">
              <p className="font-semibold">{formatMoney(r.balance_cents)}</p>
              <p className="text-xs text-muted-foreground">{r.hustle_score} pts</p>
            </div>
          </Card>
        ))}
        {!loading && rows.length === 0 && <p className="text-sm text-muted-foreground">No users found.</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>${selected?.cashtag} · {selected?.display_name}</DialogTitle>
            <DialogDescription className="text-xs">{selected?.user_id}</DialogDescription>
          </DialogHeader>

          {!detail ? (
            <div className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 my-3">
                <Card className="p-3"><p className="text-xs text-muted-foreground">Balance</p>
                  <p className="font-bold">{formatMoney(detail.wallet?.balance_cents ?? 0)}</p></Card>
                <Card className="p-3"><p className="text-xs text-muted-foreground">Hustle</p>
                  <p className="font-bold">{detail.profile?.hustle_score ?? 0} · {detail.profile?.hustle_level}</p></Card>
                <Card className="p-3"><p className="text-xs text-muted-foreground">Status</p>
                  <p className="font-bold">{detail.frozen ? "FROZEN" : "Active"}</p></Card>
              </div>

              {detail.frozen && detail.freeze && (
                <Card className="p-3 mb-3 border-destructive/50 bg-destructive/5">
                  <p className="text-xs font-semibold">Freeze reason</p>
                  <p className="text-sm">{detail.freeze.reason}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Since {new Date(detail.freeze.frozen_at).toLocaleString()}
                  </p>
                </Card>
              )}

              <div className="flex flex-wrap gap-2 mb-4">
                {detail.frozen ? (
                  <Button size="sm" variant="outline" onClick={doUnfreeze} disabled={acting}>
                    <Snowflake className="h-4 w-4" /> Unfreeze
                  </Button>
                ) : (
                  <Button size="sm" variant="destructive" onClick={() => setFreezeOpen(true)}>
                    <Snowflake className="h-4 w-4" /> Freeze
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setAdjustOpen(true)}>
                  <DollarSign className="h-4 w-4" /> Adjust balance
                </Button>
                <Button size="sm" variant="outline" onClick={doResetPin} disabled={acting}>
                  <KeyRound className="h-4 w-4" /> Reset PIN
                </Button>
              </div>

              <h3 className="font-semibold text-sm mb-2">Recent transactions</h3>
              <div className="space-y-1 mb-4 max-h-64 overflow-y-auto">
                {(detail.recent_tx ?? []).length === 0 && <p className="text-xs text-muted-foreground">None.</p>}
                {(detail.recent_tx ?? []).map((t: any) => (
                  <div key={t.id} className="flex justify-between text-xs border-b border-border py-1.5">
                    <span>{t.sender_id === selected?.user_id ? "→ sent" : "← received"} {formatMoney(t.amount_cents)}</span>
                    <span className="text-muted-foreground">{new Date(t.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <h3 className="font-semibold text-sm mb-2">Recent security events</h3>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(detail.recent_security ?? []).length === 0 && <p className="text-xs text-muted-foreground">None.</p>}
                {(detail.recent_security ?? []).map((s: any) => (
                  <div key={s.id} className="text-xs border-b border-border py-1.5">
                    <span className="font-mono">{s.event_type}</span>
                    <span className="text-muted-foreground ml-2">{new Date(s.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={freezeOpen} onOpenChange={setFreezeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Freeze wallet</DialogTitle>
            <DialogDescription>This blocks all sends, receives, withdrawals, and spins for this user.</DialogDescription>
          </DialogHeader>
          <Label>Reason (required)</Label>
          <Textarea value={freezeReason} onChange={(e) => setFreezeReason(e.target.value)} placeholder="Suspected fraud, KYC pending, etc." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFreezeOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doFreeze} disabled={acting || freezeReason.trim().length < 3}>
              {acting ? "Freezing…" : "Freeze wallet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust balance</DialogTitle>
            <DialogDescription>Use a negative amount to deduct. Always logged.</DialogDescription>
          </DialogHeader>
          <Label>Amount (USD, e.g. 5 or -2.50)</Label>
          <Input value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} placeholder="0.00" />
          <Label className="mt-2">Reason</Label>
          <Textarea value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancel</Button>
            <Button onClick={doAdjust} disabled={acting || adjustReason.trim().length < 3}>
              {acting ? "Saving…" : "Apply adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
